/**
 * `/notifications` — the student's notice board.
 *
 * Ishraf asked for this for the QED demo (2026-07-27). Teachers already post
 * course announcements (the Communicate tab writes them) and guardians could
 * already read them for their child — the student themselves had nowhere to
 * see them, which is the odder gap. Reads `learning.learner.my.announcements`,
 * own active enrolments only.
 *
 * Pinned notices sort first, then newest — a teacher pins the thing everyone
 * must not miss, so the ordering has to honour that.
 *
 * Ticket 2.5 added the date range + Read/Unread filters and the read receipts.
 * Two things follow from where the filtering happens:
 *   - It runs SERVER-side (the proc takes `from`/`to`/`status` and narrows in
 *     SQL). Filtering the fetched page in JS would answer "3 unread" off a
 *     50-row slice when 40 exist, so the filters are query inputs, not a
 *     `.filter()` over `rows`.
 *   - Which means "showing N of M" needs the unfiltered feed as its
 *     denominator — hence the baseline query below, which is also what renders
 *     when no filter is set.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  Input,
  Label,
  RichText,
  Skeleton,
  cn,
} from '@mangrove/ui';
import { Bell, ChevronDown, Pin } from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { LearnShell } from '../components/shell.js';

export const Route = createFileRoute('/notifications/')({
  component: NotificationsPage,
});

// Off the generated contract, NOT `ReturnType<typeof useQuery>` — that
// instantiates the generic with its defaults and collapses `data` to `{}`.
type RouterOutputs = inferRouterOutputs<AppRouter>;
type Notice = RouterOutputs['learning']['learner']['my']['announcements']['rows'][number];

/** Mirrors the proc's `status` enum — All shows both read states. */
type ReadFilter = 'all' | 'read' | 'unread';

const READ_FILTER_OPTIONS: ReadonlyArray<{ value: ReadFilter; label: string }> = [
  { value: 'all', label: 'All notices' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
];

const SELECT_CLASS =
  'press min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium';

/**
 * A notice is identified by (source, id): the two sources are different tables
 * and nothing stops a `notifications` row and a `course_announcements` row
 * sharing an id.
 */
function noticeKey(n: Pick<Notice, 'id' | 'source'>): string {
  return `${n.source}:${n.id}`;
}

/** "Today" / "Yesterday" / "12 Mar 2026" — relative where it helps, absolute after. */
function when(iso: string): string {
  const d = new Date(iso);
  const days = Math.round((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString();
}

function NotificationsPage(): React.JSX.Element {
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [readFilter, setReadFilter] = React.useState<ReadFilter>('all');

  const filtersActive = from !== '' || to !== '' || readFilter !== 'all';

  // The unfiltered feed: what renders with no filter set, and the honest M in
  // "showing N of M" — the filtered call can only ever return the matches.
  const allQ = trpc.learning.learner.my.announcements.useQuery(undefined);
  const filteredQ = trpc.learning.learner.my.announcements.useQuery(
    // '' is "no bound", not a date — the proc rejects anything that is not a
    // real calendar day, so an empty picker must send `undefined`.
    { from: from || undefined, to: to || undefined, status: readFilter },
    { enabled: filtersActive },
  );

  const allRows = allQ.data?.rows ?? [];
  const rows = filtersActive ? (filteredQ.data?.rows ?? []) : allRows;
  // `isLoading` (not `isPending`) — a disabled query stays pending forever and
  // would pin the skeleton on screen whenever no filter is set.
  const isLoading = filtersActive ? filteredQ.isLoading : allQ.isLoading;

  /**
   * Notices opened in THIS session. The receipt is written server-side, but the
   * list is deliberately not invalidated on success: under the Unread filter a
   * refetch would yank the notice out from under the student the instant they
   * opened it. Merging a local set instead flips the badge immediately, keeps
   * the row where it is while they read it, and the next fetch (a filter change
   * or a revisit) picks up the persisted truth.
   */
  const [locallyRead, setLocallyRead] = React.useState<ReadonlySet<string>>(() => new Set());
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(() => new Set());

  const markRead = trpc.learning.learner.my.markNoticeRead.useMutation();
  const markReadMutate = markRead.mutate;

  const toggle = React.useCallback(
    (n: Notice): void => {
      const key = noticeKey(n);
      const opening = !expanded.has(key);
      setExpanded((prev) => {
        const next = new Set(prev);
        if (opening) next.add(key);
        else next.delete(key);
        return next;
      });
      if (!opening || n.isRead || locallyRead.has(key)) return;
      setLocallyRead((prev) => new Set(prev).add(key));
      // Idempotent by construction on the server (ON CONFLICT DO NOTHING, and
      // an already-read broadcast short-circuits), so a repeat open is a
      // harmless no-op and there is nothing to roll back.
      markReadMutate({ id: n.id, source: n.source });
    },
    [expanded, locallyRead, markReadMutate],
  );

  const clearFilters = React.useCallback((): void => {
    setFrom('');
    setTo('');
    setReadFilter('all');
  }, []);

  return (
    <LearnShell>
      <div className="mx-auto w-full max-w-3xl space-y-4 p-4 sm:p-6">
        <header className="space-y-1">
          <h1 className="font-display text-2xl font-bold">Notifications</h1>
          <p className="text-sm text-muted-foreground">
            Announcements from the teachers of your courses.
          </p>
        </header>

        {allRows.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="notice-filters">
            <div className="space-y-1.5">
              <Label htmlFor="notice-from">From</Label>
              <Input
                id="notice-from"
                data-testid="notice-filter-from"
                type="date"
                className="min-h-touch"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                aria-label="Show notices posted on or after this date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notice-to">To</Label>
              <Input
                id="notice-to"
                data-testid="notice-filter-to"
                type="date"
                className="min-h-touch"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                aria-label="Show notices posted on or before this date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notice-status">Read state</Label>
              <select
                id="notice-status"
                data-testid="notice-filter-status"
                className={SELECT_CLASS}
                value={readFilter}
                onChange={(e) => setReadFilter(e.target.value as ReadFilter)}
              >
                {READ_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        {filtersActive ? (
          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span data-testid="notice-count">
              Showing {rows.length} of {allRows.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="press"
              data-testid="notice-filter-clear"
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          </div>
        ) : null}

        {isLoading ? (
          <div className="space-y-3" data-testid="notifications-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : allRows.length === 0 ? (
          <Card>
            <CardContent className="py-2">
              <EmptyState
                data-testid="notices-empty"
                icon={Bell}
                title="Nothing new"
                description="Announcements your teachers post appear here — notices about classes, exams and deadlines."
              />
            </CardContent>
          </Card>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent className="py-2">
              <EmptyState
                data-testid="notices-empty-filtered"
                icon={Bell}
                title="No notices match these filters"
                description="Try a wider date range, or set the read state back to All notices."
              />
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-3" data-testid="notifications-list">
            {rows.map((n) => {
              const key = noticeKey(n);
              const read = n.isRead || locallyRead.has(key);
              const open = expanded.has(key);
              return (
                <li key={key} data-testid="notice-row" data-source={n.source} data-read={read}>
                  <Card className={n.isPinned ? 'border-primary/40 bg-primary/5' : undefined}>
                    <CardHeader className="pb-2">
                      <button
                        type="button"
                        onClick={() => toggle(n)}
                        aria-expanded={open}
                        data-testid="notice-toggle"
                        className="press flex w-full items-start gap-3 rounded-lg text-left transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {/* The unread dot keeps its space once read, so titles
                            stay aligned all the way down the list. */}
                        <span
                          aria-hidden="true"
                          className={cn(
                            'mt-2 size-2 shrink-0 rounded-full',
                            read ? 'bg-transparent' : 'bg-primary',
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 font-semibold leading-snug">
                            {n.isPinned ? (
                              <Pin className="size-3.5 shrink-0 text-primary" aria-label="Pinned" />
                            ) : null}
                            <span className="truncate">{n.title}</span>
                          </span>
                          <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            {/* A broadcast has no course — it came from the
                                school, not from one class. */}
                            <span className="truncate">{n.courseTitle ?? 'From your school'}</span>
                            <span aria-hidden="true">·</span>
                            <span>{when(n.publishedAt)}</span>
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          <Badge
                            variant={read ? 'secondary' : 'default'}
                            data-testid="notice-read-state"
                          >
                            {read ? 'Read' : 'New'}
                          </Badge>
                          <ChevronDown
                            className={cn(
                              'size-4 text-muted-foreground transition-transform',
                              open && 'rotate-180',
                            )}
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                    </CardHeader>
                    {open ? (
                      <CardContent className="pt-0 text-sm" data-testid="notice-body">
                        <RichText text={n.body} />
                      </CardContent>
                    ) : null}
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </LearnShell>
  );
}
